import { useState, useCallback } from 'react'
import { BaseProvider, createTheme } from 'baseui'
import { Modal } from 'baseui/modal'
import AbandonModalContent from '@/shared/components/contracts/AbandonModalContent'
import selectraPrimitives from '@/shared/theme/themePrimitives'
import selectraOverrides from '@/shared/theme/themeOverrides'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import { KIND, SIZE } from 'baseui/button'
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid'
import DevToolsSearch from './DevToolsSearch'
import PropTypes from 'prop-types'
import SubscribersList from './SubscribersList'
import useHttp from '@/shared/hooks/useHttp'
import ValidationStatusBadge from '@/shared/components/FormHeader/bones/ValidationStatusBadge'
import { useTranslation } from 'react-i18next'
import IconCarbonRotate360 from '@/shared/icons/IconCarbonRotate360'
import { Check } from 'baseui/icon'
import useBusinessType from '@/shared/hooks/useBusinessType'
import IconCustomZoho from '@/shared/icons/IconCustomZoho'
import SubscribersToolsErrorNotification from '@/containers/DevTools/SubscribersToolsErrorNotification'
import EditableCell from '@/containers/DevTools/EditableCell'
import { useSelector } from 'react-redux'
import { selectAuthData } from '@/containers/Login/AuthSelectors'

const SubscribersTools = ({ urls }) => {
  const { t } = useTranslation()
  const { lang, extraRoutes } = useSelector(selectAuthData)
  const [status, setStatus] = useState({})
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [nextCursor, setNextCursor] = useState(null)
  const [prevCursor, setPrevCursor] = useState(null)
  const { sendRequest, isLoading, getConfig } = useHttp()
  const [subscriberAction, setSubscriberAction] = useState(0)
  const { getApplicationTypeFolder } = useBusinessType({ loaderRoutesEnergy: {}, loaderRoutes: {} })
  const folderType = getApplicationTypeFolder()
  const productType = (folderType || '').toLowerCase()
  const flexGridItemProps = {
    display: 'flex',
    alignItems: 'center',
  }
  const [subscribers, setSubscribers] = useState([])
  const [abandonModalOpen, setAbandonModalOpen] = useState(false)
  const [abandonTarget, setAbandonTarget] = useState(null)
  const selectraTheme = createTheme(selectraPrimitives, selectraOverrides)
  const requestStatus = () => {
    if (status && Object.keys(status).length !== 0) {
      return <ValidationStatusBadge text={status.text} status={status.colour} />
    }
  }
  const requestResponseHandler = response => {
    if (response.error === false || response.status === 'ok') {
      setError(null)
      setStatus({ text: t('devTools:action_result_ok'), colour: 'positive' })
    } else {
      setError(response.error)
      setStatus({ text: t('devTools:action_result_ko'), colour: 'negative' })
    }
  }
  const requestSubscriptionsHandler = response => {
    setSubscribers(response.subscribers.data)
    setNextCursor(response.subscribers.next_cursor ?? null)
    setPrevCursor(response.subscribers.prev_cursor ?? null)
  }
  const signToNetStatus = subscriber => {
    setError(null)
    setStatus({})
    setSubscriberAction(subscriber?.id)
    sendRequest(
      urls.subscriptions_sign_to_net_status_url +
        '/' +
        (subscriber?.id ?? 0) +
        '?lang=' +
        lang +
        '&businessType=' +
      productType,
      getConfig('PUT'),
      requestResponseHandler,
    )
  }
  const requestSearchHandler = (searchTerm, cursor = null) => {
    setError(null)
    setStatus({})
    setSearchTerm(searchTerm)
    if (! cursor) {
      setNextCursor(null)
      setPrevCursor(null)
    }
    sendRequest(
      urls.subscriptions_search + '/' + productType + '/' + searchTerm + '?lang=' + lang + (cursor ? '&cursor=' + cursor : ''),
      getConfig('GET'),
      requestSubscriptionsHandler,
    )
  }
  const resetExportState = subscriber => {
    setError(null)
    setStatus({})
    sendRequest(
      urls.subscriptions_reset_export_status_url + '/' + (subscriber?.id ?? 0) + '?lang=' + lang,
      getConfig('PUT'),
      requestResponseHandler,
    )
  }
  const openAbandonModal = subscriber => {
    setAbandonTarget(subscriber)
    setAbandonModalOpen(true)
  }
  const submitAbandon = useCallback((reason, callback) => {
    if (!abandonTarget) return
    setError(null)
    setStatus({})
    setSubscriberAction(abandonTarget.id)
    sendRequest(
      urls.abandonUrl.replace('*', String(abandonTarget?.id ?? 0)),
      {
        ...getConfig('POST'),
        body: JSON.stringify({ comment: reason, subscriber_id: abandonTarget?.id }),
        headers: { ...getConfig('POST').headers, 'Content-Type': 'application/json' },
      },
      response => {
        const success = response.success === true || response.error === false
        callback(success)
        if (success) {
          setAbandonModalOpen(false)
          setAbandonTarget(null)
          setStatus({ text: t('devTools:action_result_ok'), colour: 'positive' })
          if (searchTerm) requestSearchHandler(searchTerm)
        } else {
          setError(response.error)
          setStatus({ text: t('devTools:action_result_ko'), colour: 'negative' })
        }
      },
    )
  }, [abandonTarget, urls, lang, productType, searchTerm])
  const updateProviderExternalId = (subscriber, newValue) => {
    setError(null)
    setStatus({})
    const url = extraRoutes?.devTools?.contractsUpdateProviderExternalId
    if (!url) return
    sendRequest(
      url,
      {
        ...getConfig('POST'),
        body: JSON.stringify({
          provider_external_id: newValue,
          subscriber_id: subscriber?.id ?? null,
          contract_type: productType || null,
        }),
        headers: { ...getConfig('POST').headers, 'Content-Type': 'application/json' },
      },
      requestResponseHandler,
    )
  }
  const reSyncToZoho = subscriber => {
    setError(null)
    setStatus({})
    setSubscriberAction(subscriber?.id)
    sendRequest(
      urls.subscriptions_sync_to_zoho_url + '/' + productType + '/' + (subscriber?.id ?? 0) + '/1' + '?lang=' + lang,
      getConfig('PUT'),
      requestResponseHandler,
    )
  }
  const getStatus = (subscriber, productType) => {
    if (productType === 'energy') {
      return subscriber?.product_energy?.subscriber_status ?? ''
    }
    if (productType === 'insurance') {
      return subscriber?.product_insurance?.subscriber_status ?? ''
    }
    return subscriber?.product_telecom?.subscriber_status ?? ''
  }
  const getExportState = (subscriber, productType) => {
    if (productType === 'energy') {
      return subscriber?.product_energy?.zoho_export_state ?? ''
    }
    if (productType === 'insurance') {
      return subscriber?.product_insurance?.zoho_export_state ?? ''
    }
    return subscriber?.product_telecom?.zoho_export_state ?? ''
  }
  const getProviderExternalId = (subscriber, productType) => {
    if (productType === 'energy') {
      return subscriber?.product_energy?.provider_external_id ?? ''
    }
    if (productType === 'insurance') {
      return subscriber?.product_insurance?.provider_external_id ?? ''
    }
    return subscriber?.product_telecom?.provider_external_id ?? ''
  }
  const transformSubscribers = subscribersList => {
    if (!subscribersList) {
      return []
    }
    return subscribersList.map(subscriber => {
      const status = getStatus(subscriber, productType)
      const exportState = getExportState(subscriber, productType)
      const providerExternalId = getProviderExternalId(subscriber, productType)
      return [
        subscriber.id,
        subscriber.email,
        subscriber.email_token,
        subscriber.firstname,
        subscriber.lastname,
        <EditableCell
          key={`peid-${subscriber.id}`}
          value={providerExternalId}
          onSave={newValue => updateProviderExternalId(subscriber, newValue)}
        />,
        status,
        exportState,
        new Date(subscriber.created_at).toLocaleString(),
        <SDButton
          key={subscriber.id}
          block
          size={SIZE.mini}
          kind={KIND.secondary}
          startEnhancer={() => <IconCarbonRotate360 size={16} />}
          onClick={() => resetExportState(subscriber)}
        >
          {t('devTools:action_export_state')}
        </SDButton>,
        <SDButton
          key={subscriber.id}
          block
          size={SIZE.mini}
          kind={KIND.secondary}
          startEnhancer={() => <Check size={16} />}
          onClick={() => signToNetStatus(subscriber)}
          isLoading={isLoading && subscriberAction === subscriber.id}
        >
          {t('devTools:action_sign_to_net')}
        </SDButton>,
        <SDButton
          key={subscriber.id}
          block
          size={SIZE.mini}
          kind={KIND.secondary}
          onClick={() => reSyncToZoho(subscriber)}
          startEnhancer={() => <IconCustomZoho size={16} />}
          isLoading={isLoading && subscriberAction === subscriber.id}
        >
          {t('devTools:action_sync_zoho')}
        </SDButton>,
        <SDButton
          key={subscriber.id}
          block
          size={SIZE.mini}
          kind={KIND.secondary}
          onClick={() => openAbandonModal(subscriber)}
        >
          {t('devTools:action_sign_to_abandon')}
        </SDButton>,
      ]
    })
  }
  const Paginator = () => {
    if (! nextCursor && ! prevCursor) {
      return null
    }
    return (
      <FlexGrid flexGridColumnCount={2} marginTop='.5rem' flexGridColumnGap='scale800'>
        <FlexGridItem>
          {prevCursor && (
            <SDButton
              size={SIZE.mini}
              kind={KIND.secondary}
              onClick={() => requestSearchHandler(searchTerm, prevCursor)}
            >
              {t('devTools:pagination_prev')}
            </SDButton>
          )}
        </FlexGridItem>
        <FlexGridItem>
          {nextCursor && (
            <SDButton
              size={SIZE.mini}
              kind={KIND.secondary}
              onClick={() => requestSearchHandler(searchTerm, nextCursor)}
            >
              {t('devTools:pagination_next')}
            </SDButton>
          )}
        </FlexGridItem>
      </FlexGrid>
    )
  }

  return (
    <BaseProvider theme={selectraTheme}>
      <FlexGrid flexGridColumnCount={2} marginBottom='.5rem' flexGridColumnGap='scale800'>
        <FlexGridItem {...flexGridItemProps}>
          <DevToolsSearch
            requestSearchHandler={value => requestSearchHandler(value)}
            placeholder={'Rechercher dans ce tableau par le email token'}
          />
        </FlexGridItem>
        <FlexGridItem {...flexGridItemProps}>{requestStatus()}</FlexGridItem>
      </FlexGrid>
      <FlexGrid>
        <FlexGridItem {...flexGridItemProps}>
          <SubscribersToolsErrorNotification error={error} />
        </FlexGridItem>
      </FlexGrid>
      <SubscribersList items={transformSubscribers(subscribers)} />
      <Paginator />
      <Modal isOpen={abandonModalOpen} onClose={() => setAbandonModalOpen(false)}>
        <AbandonModalContent onSubmit={submitAbandon} minLength={10} />
      </Modal>
    </BaseProvider>
  )
}

SubscribersTools.propTypes = {
  urls: PropTypes.object,
}

export default SubscribersTools
